import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Users, Loader2, RefreshCw, ChevronDown, ChevronUp, Clapperboard, CheckCircle, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { VideosModal } from './BranchListings';
import { AssignmentCard, openClipsInEditor } from './EditorListings';
import './EditorListings.css';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EditorTeam({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [videosModal, setVideosModal] = useState(null);
  const pollRef = useRef(null);
  const pollCount = useRef(0);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/api/editor-hub/team`);
      setData(res.data);
      clearTimeout(pollRef.current);
      if (res.data.syncing && pollCount.current < 30) {
        pollCount.current += 1;
        pollRef.current = setTimeout(() => load(true), 4000);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load team overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => clearTimeout(pollRef.current);
  }, [load]);

  const refresh = async () => {
    try {
      await axios.post(`${API}/api/editor-hub/refresh`);
      toast.success('Syncing with WMS…');
      setTimeout(() => load(true), 1500);
    } catch {
      toast.error('Refresh failed');
    }
  };

  if (loading) {
    return <div className="el-loading"><Loader2 size={28} className="animate-spin" /><span>Loading team…</span></div>;
  }

  const members = data?.members || [];
  const totalQueue = members.reduce((s, m) => s + m.to_edit, 0);

  return (
    <div className="editor-team" data-testid="editor-team">
      <div className="el-toolbar">
        <div className="et-summary">
          <Users size={16} />
          <span>{members.length} editors · <strong>{totalQueue}</strong> orders in queue</span>
        </div>
        <div className="el-toolbar-right">
          {data?.syncing && <span className="el-syncing"><Loader2 size={13} className="animate-spin" /> Syncing WMS…</span>}
          <button className="el-refresh-btn" onClick={refresh} data-testid="et-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="et-members">
        {members.map(m => {
          const open = expanded === m.wms_editor_id;
          return (
            <div key={m.wms_editor_id} className={`et-member ${open ? 'is-open' : ''}`} data-testid={`et-member-${m.wms_editor_id}`}>
              <button className="et-member-row" onClick={() => setExpanded(open ? null : m.wms_editor_id)} data-testid={`et-member-toggle-${m.wms_editor_id}`}>
                <div className="et-avatar">{m.name.split(' ').map(w => w[0]).slice(0, 2).join('')}</div>
                <div className="et-member-info">
                  <span className="et-member-name">{m.name} {m.editor_admin && <Crown size={12} className="et-crown" />}</span>
                  <span className="et-member-email">{m.email}</span>
                </div>
                <div className="et-member-counts">
                  <span className="et-count et-count-edit"><Clapperboard size={12} /> {m.to_edit} to edit</span>
                  <span className="et-count et-count-done"><CheckCircle size={12} /> {m.complete} done</span>
                </div>
                {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {open && (
                <div className="et-member-listings">
                  {m.listings.length === 0 ? (
                    <div className="el-empty el-empty-inline"><span>No assigned video orders.</span></div>
                  ) : (
                    <div className="el-grid">
                      {m.listings.map((l, i) => (
                        <AssignmentCard
                          key={l.order_id}
                          listing={l}
                          delay={Math.min(i * 30, 300)}
                          onShowVideos={() => setVideosModal({ listing: l })}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {videosModal && (
        <VideosModal
          listing={videosModal.listing}
          onClose={() => setVideosModal(null)}
          onOpenEditor={(opts) => {
            openClipsInEditor(videosModal.listing.order_id, opts, onNavigate);
            setVideosModal(null);
          }}
        />
      )}
    </div>
  );
}
